import dayjs from 'dayjs';
import type { UndefinedOnPartialDeep } from 'type-fest';

import type { DtoPickupPointAddress, DtoSaving } from '@repo/api-types/order-retrieval';
import { translate } from '@repo/phrase';
import { type CustomerData } from '@repo/types/customer';
import { orderStatuses } from '@repo/types/order';
import { type Service, pickupServiceTypes, serviceTypes } from '@repo/types/service';
import { formatDate, formatDateTime, formatTime } from '@repo/utils';
import { hasProperty, isObject } from '@repo/utils/type.utils';

import { getAppConfig } from '@/utils/window.utils';

const deliveryOrderStatuses = {
    [orderStatuses.HasAnIssue]: [
        '1300',
        '9000',
        '3200.135',
        '3700.01.100',
        '3700.02.100',
        '3700.045',
        '3700.050',
        '3700.080',
        '3700.100',
        '3700.130',
        '3700.140',
        '3700.160',
        '3200.145',
        '1100.120',
        '1100.130',
        '1100.140',
        '3700.046',
        '1100.110',
    ],
    [orderStatuses.Booked]: ['1100', '1500.020', '3200.020', '1100.001'],
    [orderStatuses.InProgress]: [
        '3200.060',
        '3200.100',
        '3200.130',
        '3700.016',
        '3700.030',
        '3200.120',
        '3350.020',
        '3700.020',
        '3700.040',
        '3700.060',
        '3700.035',
        '3700.036',
        '3700.110',
        '3700.150',
        '3200.140',
        '2130.01.040',
        '3700.014',
        '3200.040',
        '3200.080',
        '1600.020',
        '1600.040',
        '2030.020',
        '2100.030',
        '2100.050',
        '2130.0050.040',
        '3700.010',
        '2130.01.020',
        '2100.010',
        '2130.0050.020',
        '3700.120',
    ],
    [orderStatuses.Completed]: ['3700.7777.010', '3700.7777', '3700.7777.040', '3700.7777.050'],
};

export const getOrderStatus = (statusCode: string | undefined): string => {
    // If there is no statusCode "Not found" status is returned that is used either as a differentiator of a delivery icon or as a part of the translation key in <ArticleDeliveryStatusIcon> component in order to display the message in user language
    if (!statusCode) {
        return orderStatuses.NotFound;
    }

    for (const [status, codes] of Object.entries(deliveryOrderStatuses)) {
        if (codes.includes(statusCode)) {
            return status;
        }
    }

    // If statusCode wasn't found in deliveryOrderStatuses "Not found" is returned that is used either as a differentiator of a delivery icon or as a part of the translation key in <ArticleDeliveryStatusIcon> component in order to display the message in user language
    return orderStatuses.NotFound;
};

export const getDeliveryType = (serviceType: string | undefined): string => {
    switch (serviceType) {
        case serviceTypes.HOME_DELIVERY: {
            return translate('services_article_delivery_view_delivery_type_HOME_DELIVERY');
        }
        case serviceTypes.CLICK_COLLECT_STORE: {
            return translate('services_article_delivery_view_delivery_type_CLICK_COLLECT_STORE');
        }
        case serviceTypes.PUP:
        case serviceTypes.PUOP:
        case serviceTypes.LOCKER: {
            return translate('services_article_delivery_view_delivery_type_PUP');
        }
        default: {
            return translate('services_article_delivery_view_delivery_type_NOT_FOUND');
        }
    }
};

export const formatDeliveryAddress = (
    address: UndefinedOnPartialDeep<DtoPickupPointAddress> | UndefinedOnPartialDeep<CustomerData>
) => {
    const isPickupPointAddress = (address: unknown): address is DtoPickupPointAddress =>
        isObject(address) && !('ikeaFamilyNumber' in address);

    const { addressFormatStandard, addressFormatPuP } = getAppConfig();

    const addressFormat = isPickupPointAddress(address) ? addressFormatPuP : addressFormatStandard;

    return addressFormat
        .replace(/\{\{(\w+)\}\}/g, (_value, key) => {
            if (!hasProperty(address, key) || !address[key]) {
                return '';
            }

            return address[key];
        })
        .replace(/\s*,\s*/g, ', ') // Normalize comma spacing
        .replace(/^,\s*|,\s*$/g, '') // Remove leading/trailing commas
        .replace(/,\s*,+/g, ',') // Remove consecutive commas
        .trim();
};

const getServiceArrivalTimeDetails = (timeWindow: Service['timeWindow'], isDelivery: Service['isDelivery']) => {
    if (!timeWindow?.fromDateTimeUtc || !timeWindow.toDateTimeUtc) {
        return isDelivery
            ? translate('services_article_delivery_view_delivery_time_NOT_FOUND')
            : translate('services_article_service_view_service_time_NOT_FOUND');
    }

    const { fromDateTimeUtc, toDateTimeUtc, timeZone } = timeWindow;
    const { dateTimeFormat, dateFormat, timeFormat } = getAppConfig();

    const serviceArrivalDateFrom = formatDateTime(fromDateTimeUtc, dateTimeFormat, timeZone);
    const serviceArrivalDayFrom = formatDate(fromDateTimeUtc, dateFormat, timeZone);
    const serviceArrivalHourFrom = formatTime(fromDateTimeUtc, timeFormat, timeZone);
    const serviceArrivalDateTo = formatDateTime(toDateTimeUtc, dateTimeFormat, timeZone);
    const serviceArrivalDayTo = formatDate(toDateTimeUtc, dateFormat, timeZone);
    const serviceArrivalHourTo = formatTime(toDateTimeUtc, timeFormat, timeZone);

    return {
        serviceArrivalDateFrom,
        serviceArrivalDayFrom,
        serviceArrivalHourFrom,
        serviceArrivalDateTo,
        serviceArrivalDayTo,
        serviceArrivalHourTo,
    };
};

export const getEstimatedServiceArrivalTime = (
    timeWindow: Service['timeWindow'],
    isDelivery: Service['isDelivery']
) => {
    const serviceArrivalTimeDetails = getServiceArrivalTimeDetails(timeWindow, isDelivery);

    if (typeof serviceArrivalTimeDetails === 'string') {
        return serviceArrivalTimeDetails;
    }

    const { serviceArrivalDayFrom, serviceArrivalHourFrom, serviceArrivalDayTo, serviceArrivalHourTo } =
        serviceArrivalTimeDetails;

    let translationKey: string;
    let translationParams: Record<string, string>;

    if (serviceArrivalDayFrom === serviceArrivalDayTo) {
        translationKey = 'services_estimated_service_arrival_same_day';
        translationParams = {
            serviceArrivalDayFrom: serviceArrivalDayFrom,
            serviceArrivalHourFrom: serviceArrivalHourFrom,
            serviceArrivalHourTo: serviceArrivalHourTo,
        };
    } else {
        translationKey = 'services_estimated_service_arrival_different_day';
        translationParams = {
            serviceArrivalDayFrom: serviceArrivalDayFrom,
            serviceArrivalHourFrom: serviceArrivalHourFrom,
            serviceArrivalDayTo: serviceArrivalDayTo,
            serviceArrivalHourTo: serviceArrivalHourTo,
        };
    }

    return translate(translationKey, translationParams);
};

export const formatIconName = (iconName: string) => {
    // if orderStatus received by component is of OrderStatus type, then it can be safely assumed that it is one of the keys of statusIcons object and corresponding icon can be assigned to iconPath
    // iconPath object has 'name' property which is a string of the format "iconName_default" (e.g. "arrow_on_path_default")
    // so to dynamically pick the icon name to be used in testId part of the name before "_default" is picked and all underscores are replaced with hyphens
    return iconName.split('_default')[0]?.replace(/_/g, '-');
};

export const formatServiceTimeWindow = (isDelivery: Service['isDelivery'], timeWindow: Service['timeWindow']) => {
    const serviceArrivalTimeDetails = getServiceArrivalTimeDetails(timeWindow, isDelivery);

    if (typeof serviceArrivalTimeDetails === 'string') {
        return serviceArrivalTimeDetails;
    }

    const {
        serviceArrivalDateFrom,
        serviceArrivalDayFrom,
        serviceArrivalHourFrom,
        serviceArrivalDateTo,
        serviceArrivalDayTo,
        serviceArrivalHourTo,
    } = serviceArrivalTimeDetails;

    if (serviceArrivalDayFrom === serviceArrivalDayTo) {
        return `${serviceArrivalDayFrom}, ${serviceArrivalHourFrom} - ${serviceArrivalHourTo}`;
    } else {
        return `${serviceArrivalDateFrom} - ${serviceArrivalDateTo}`;
    }
};

interface FulfillmentUnitSummary {
    readonly code?: string | undefined;
    readonly type?: string | undefined;
}

export const formatFulfillmentUnit = (fulfillmentUnit: FulfillmentUnitSummary | undefined, separator = ' '): string => {
    if (!fulfillmentUnit?.type || !fulfillmentUnit.code) {
        return '';
    }

    return `${fulfillmentUnit.type}${separator}${fulfillmentUnit.code}`;
};

export const formatServiceSummaryDetails = (
    serviceProviderName: string | undefined,
    fulfillmentUnit: FulfillmentUnitSummary | undefined
): string => {
    const fulfillmentUnitText = formatFulfillmentUnit(fulfillmentUnit);

    return [serviceProviderName, fulfillmentUnitText]
        .filter((value) => value !== undefined && value.length > 0)
        .join(' | ');
};

export const formatServiceStatus = (status: Service['status']) => {
    if (!status) {
        return 'NOT_FOUND';
    }

    return status.toUpperCase();
};

export const extractEntitySavings = (
    entity: UndefinedOnPartialDeep<{ savings?: DtoSaving[] }>
): Array<UndefinedOnPartialDeep<DtoSaving>> => {
    if (!('savings' in entity) || !Array.isArray(entity.savings)) {
        return [];
    }

    return entity.savings.filter(Boolean);
};

export const getServicePrice = (
    service: UndefinedOnPartialDeep<Service>,
    priceValueField: 'priceExclTax' | 'priceInclTax'
) => {
    if (
        service.price &&
        'amount' in service.price &&
        typeof service.price.amount === 'number' &&
        service.price.amount > 0
    ) {
        return service.price.amount;
    }

    return service.price?.[priceValueField] ?? 0;
};

export const getServiceTimeZone = (service: UndefinedOnPartialDeep<Service>) => service.timeWindow?.timeZone ?? 'UTC';

export const getServiceTimeWindowStartUtc = (service: UndefinedOnPartialDeep<Service>) =>
    dayjs.utc(service.timeWindow?.fromDateTimeUtc);

export const getServiceUniqueKey = (service: UndefinedOnPartialDeep<Service>): string | undefined =>
    service.serviceInstanceId ??
    (service.deliveryNo ? `${service.serviceId}-${service.deliveryNo}` : service.serviceId);

export const isFullServeHandoutDelivery = (service: UndefinedOnPartialDeep<Service>): boolean => {
    const { serviceType, serviceId } = service;

    return (
        serviceType === pickupServiceTypes.CLICK_COLLECT_STORE &&
        (serviceId === '20001032' || serviceId === 'SGR20001032')
    );
};
